/**
 * Modal bottom-sheet para registrar pagamento de mensalidade.
 *
 * Estado interno: mantém os próprios campos (amount, method, date,
 * notes, submitting) — não vaza pro orquestrador. Isso simplifica o
 * pai (FinanceSection) que só precisa controlar `visible`.
 *
 * Props:
 *   - visible: controla mount/unmount do Modal
 *   - defaultAmount: valor inicial sugerido (vem da mensalidade)
 *   - onClose: fechar sem salvar
 *   - onSubmit: chamado com os dados do pagamento. Retorna Promise<void>;
 *     se rejeitar, modal NÃO fecha (deixa user tentar de novo).
 *
 * Decisões:
 *   - Data como TextInput numérico — simples, sem date picker (mobile
 *     teclado numérico já é OK pra AAAA-MM-DD). Trocar quando ficar UX-priority.
 *   - Reset dos campos só acontece ao fechar com sucesso. Se user
 *     cancelar e abrir de novo, recupera o que digitou.
 */

import { memo, useEffect, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, Modal, StyleSheet, KeyboardAvoidingView, Platform, ScrollView } from 'react-native';
import { PaymentMethod } from '@/lib/types';

interface PaymentSubmitData {
  amount: number;
  method: PaymentMethod;
  paidAt: string;
  notes: string | undefined;
}

interface Props {
  visible: boolean;
  defaultAmount: string;
  onClose: () => void;
  onSubmit: (data: PaymentSubmitData) => Promise<void>;
}

const METHODS: PaymentMethod[] = ['pix', 'dinheiro', 'cartão', 'transferência'];

function PaymentModalImpl({ visible, defaultAmount, onClose, onSubmit }: Props) {
  const [amount, setAmount] = useState(defaultAmount);
  const [method, setMethod] = useState<PaymentMethod>('pix');
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Sincroniza defaultAmount quando o modal abre (mensalidade pode ter mudado).
  useEffect(() => {
    if (visible) setAmount(defaultAmount);
  }, [visible, defaultAmount]);

  async function handleConfirm() {
    const parsed = parseFloat(amount.replace(',', '.'));
    if (isNaN(parsed) || parsed <= 0) return;
    setSubmitting(true);
    try {
      await onSubmit({
        amount: parsed,
        method,
        paidAt: date,
        notes: notes.trim() || undefined,
      });
      // Sucesso → reseta estado e deixa o pai fechar.
      setAmount(defaultAmount);
      setMethod('pix');
      setNotes('');
      setDate(new Date().toISOString().split('T')[0]);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <KeyboardAvoidingView
        style={styles.overlay}
        behavior="padding"
      >
        <View style={styles.sheet}>
          <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
          <Text style={styles.title}>Registrar Pagamento</Text>

          <Text style={styles.label}>Valor (R$)</Text>
          <TextInput
            style={styles.input}
            value={amount}
            onChangeText={setAmount}
            keyboardType="decimal-pad"
            placeholder="0,00"
          />

          <Text style={styles.label}>Método</Text>
          <View style={styles.methodRow}>
            {METHODS.map(m => (
              <TouchableOpacity
                key={m}
                style={[styles.methodBtn, method === m && styles.methodBtnActive]}
                onPress={() => setMethod(m)}
              >
                <Text style={[styles.methodBtnText, method === m && styles.methodBtnTextActive]}>
                  {m}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={styles.label}>Data</Text>
          <TextInput
            style={styles.input}
            value={date}
            onChangeText={setDate}
            placeholder="AAAA-MM-DD"
            keyboardType="numeric"
          />

          <Text style={styles.label}>Observação (opcional)</Text>
          <TextInput
            style={styles.input}
            value={notes}
            onChangeText={setNotes}
            placeholder="Ex: pago via app"
          />

          <View style={styles.actions}>
            <TouchableOpacity style={styles.cancel} onPress={onClose}>
              <Text style={styles.cancelText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.confirm, submitting && { opacity: 0.5 }]}
              onPress={handleConfirm}
              disabled={submitting}
            >
              <Text style={styles.confirmText}>
                {submitting ? 'Salvando...' : 'Confirmar'}
              </Text>
            </TouchableOpacity>
          </View>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  sheet: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 24,
    paddingBottom: 40,
    maxHeight: '90%',
  },
  title: { fontSize: 18, fontWeight: '700', color: '#1A1A1A', marginBottom: 16 },
  label: { fontSize: 12, fontWeight: '600', color: '#888', marginBottom: 4, marginTop: 12 },
  input: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#1A1A1A',
  },
  methodRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  methodBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  methodBtnActive: { backgroundColor: '#C8102E', borderColor: '#C8102E' },
  methodBtnText: { fontSize: 12, color: '#888', textTransform: 'capitalize' },
  methodBtnTextActive: { color: '#fff', fontWeight: '700' },
  actions: { flexDirection: 'row', gap: 10, marginTop: 20 },
  cancel: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    alignItems: 'center',
  },
  cancelText: { fontSize: 14, fontWeight: '600', color: '#888' },
  confirm: { flex: 1, padding: 12, borderRadius: 8, backgroundColor: '#C8102E', alignItems: 'center' },
  confirmText: { fontSize: 14, fontWeight: '700', color: '#fff' },
});

export const PaymentModal = memo(PaymentModalImpl);
